'use client';

import {Spinner} from '@components/Spinner';
import {useSignIn} from '@hooks/useSignIn';
import {useSession} from 'next-auth/react';
import {useRouter, useSearchParams} from 'next/navigation';
import type {FC} from 'react';
import {useLayoutEffect} from 'react';

const Login: FC = () => {
  const {data: session, status} = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {trigger: signIn, isMutating, reset} = useSignIn();

  useLayoutEffect(() => {
    // next-auth is still loading
    if (status === `loading`) return () => {};
    // We triggered the sign in action already
    if (isMutating) return () => reset();

    const params = Object.fromEntries(searchParams);
    // Remove 'callbackUrl' query param if it's 'logout' to avoid redirecting to the logout page
    if (params.callbackUrl === `/logout`) {
      delete params.callbackUrl;
    }

    if (status === `authenticated`) {
      // A tenant-scoped login link asks for a specific Auth0 organization. Honouring it requires a
      // round trip through Auth0, since the tenant is baked into the session claims - otherwise the
      // user silently stays in the tenant they were already signed in to.
      const isSwitchingTenant =
        (!!params.organization && params.organization !== session?.tenant?.id) ||
        (!!params.organization_name && params.organization_name !== session?.tenant?.slug);

      // Nothing to switch to, so the middleware / root page.tsx will redirect based on the user role
      if (!isSwitchingTenant) return () => {};
    }

    signIn({params});

    return () => reset();
  }, [isMutating, reset, router, status, signIn, searchParams, session]);

  return <Spinner />;
};

export default Login;
